import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router, UrlTree } from '@angular/router';
import { isGuestSession } from '../auth/guest-session';
import { AuthService } from '../services/auth.service';

/**
 * Keeps a share-link guest inside their share link: any app page sends them to the sign-in screen.
 *
 * Their session is deliberately left alone — no logout — so Back returns them to `/s/:id` without
 * signing in again. Returning a `UrlTree` makes the redirect replace the requested URL in the
 * browser history rather than stack on top of it, which is what keeps Back pointing at the share link.
 *
 * Goes first in `canActivate` of every app route under `MainLayoutComponent`, ahead of `AuthGuard`
 * (a guest holds a valid token and a legacy `auth_user`, so `AuthGuard` alone lets them in), and on
 * the routes that have no `AuthGuard` at all. Not on `short/:slug`: that guard only redirects to the
 * link's target, whose own route decides — a short link to the guest's own `/s/:id` must keep working.
 * The public legacy `/review` and `/share` links are left open to guests, like to anonymous visitors.
 */
export const guestConfinementGuard: CanActivateFn = (): boolean | UrlTree =>
  isGuestSession() ? inject(Router).createUrlTree(['/entry/login']) : true;

/**
 * Matches every visitor the app shell turns away — a share-link guest ({@link guestConfinementGuard})
 * or anyone without a valid session (`AuthGuard`, whose check this mirrors) — for a route they get in
 * place of the app-shell one, declared just before it on the same path. Both still have to see
 * `/unavailable`: a guest whose link no longer exists, and an anonymous visitor the sign-in screen
 * sends there for an expired share link.
 */
export const outsideAppShellMatch: CanMatchFn = (): boolean => {
  const auth: AuthService = inject(AuthService);
  return isGuestSession() || auth.getAuthUser() === null || auth.isTokenExpired();
};
